import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { fetchFastApi } from "../api/fastapi";

const PhaseOptionSchema = z.object({
  optroTestId: z.number(),
  name: z.string(),
  selectable: z.boolean(),
  reason: z.string().nullable(),
});

export type PhaseOption = z.infer<typeof PhaseOptionSchema>;

const OptroTestOptionsResponseSchema = z.object({
  linked: z.boolean(),
  options: z.array(PhaseOptionSchema),
});

export type OptroTestOptionsResponse = z.infer<
  typeof OptroTestOptionsResponseSchema
>;

/** A linked control with zero options must NOT fall back to unlinked mode — a
 * test created there has no optro_test_id and is invisible to Optro. Gated on
 * `open` so each reopen refetches. */
export const useOptroTestOptions = (controlId: string, open: boolean) => {
  const [data, setData] = useState<OptroTestOptionsResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // Closed: nothing to show; the next open resets and refetches below.
    if (!open) return;
    let cancelled = false;
    // Reset BEFORE fetching: the dialog instance survives control switches,
    // so the previous control's verdict could enable the wrong mode while a
    // fetch is in flight. `cancelled` guards out-of-order responses.
    setData(null);
    setFailed(false);
    fetchFastApi({
      method: "GET",
      path: `/controls/v2-optro/control/${encodeURIComponent(controlId)}/test-options`,
    })
      .then((res) => {
        if (cancelled) return;
        setData(OptroTestOptionsResponseSchema.parse(res));
      })
      .catch((err) => {
        console.error("Failed to load Optro test options:", err);
        if (cancelled) return;
        setFailed(true);
        // Sentry init is prod-only, so this is a no-op in dev. Extras stay
        // ids-only. The fixed toast id collapses repeat failures (reopen
        // spam) into a single toast; each failure still gets its own event.
        Sentry.captureException(err, {
          tags: { source: "optroTestOptions" },
          extra: { controlId },
        });
        toast.error("Couldn't load Optro test phases", {
          id: "optro-test-options-error",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [controlId, open]);

  return { data, failed };
};
